import { useCallback, useRef, useState } from 'react';
import { Scenario, ScenarioStep } from '../lib/types';

export interface UseScenarioResult {
  activeScenario: Scenario | null;
  currentStep: ScenarioStep | null;
  stepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  start: (scenario: Scenario) => void;
  exit: () => void;
  next: () => void;
  prev: () => void;
  goToStep: (i: number) => void;
}

export const useScenario = (): UseScenarioResult => {
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  // Hold the latest activeScenario in a ref so functional setters always see
  // the freshest value, even when callbacks fire faster than React commits a
  // render. Without this, `next`/`goToStep` would close over the
  // activeScenario captured at render time — and a sequence like
  // `start(s); next(); next();` invoked synchronously inside one event
  // handler would compute the second `next` against the pre-start `null`
  // scenario, dropping the step. Writing to the ref synchronously inside
  // `start` (and clearing in `exit`) keeps the ref aligned with the most
  // recent caller intent at all times.
  const activeScenarioRef = useRef<Scenario | null>(null);
  activeScenarioRef.current = activeScenario;

  const start = useCallback((scenario: Scenario) => {
    activeScenarioRef.current = scenario;
    setActiveScenario(scenario);
    setStepIndex(0);
  }, []);

  const exit = useCallback(() => {
    activeScenarioRef.current = null;
    setActiveScenario(null);
    setStepIndex(0);
  }, []);

  const next = useCallback(() => {
    setStepIndex((i) => {
      const scenario = activeScenarioRef.current;
      if (!scenario) return i;
      return Math.min(i + 1, scenario.steps.length - 1);
    });
  }, []);

  const prev = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goToStep = useCallback((i: number) => {
    setStepIndex(() => {
      const scenario = activeScenarioRef.current;
      if (!scenario) return 0;
      return Math.max(0, Math.min(i, scenario.steps.length - 1));
    });
  }, []);

  const totalSteps = activeScenario?.steps.length ?? 0;
  const currentStep = activeScenario?.steps[stepIndex] ?? null;
  const isFirstStep = stepIndex === 0;
  const isLastStep = totalSteps > 0 && stepIndex === totalSteps - 1;

  return {
    activeScenario,
    currentStep,
    stepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    start,
    exit,
    next,
    prev,
    goToStep,
  };
};
